import { IUser } from '@reverbdotcom/commons/src/components/user_context_provider';
import gql from 'graphql-tag';
import { AFFIRM_PUBLIC_API_KEY } from '@reverbdotcom/env';
import { isEmpty, omitBy } from 'lodash';
import {
  AffirmCheckoutBuilderFields,
  core_apimessages_Address as Address,
  core_apimessages_CheckoutLineItem_Type as CheckoutLineItemType,
} from '../schemas/rql';
import { Paths, withWebHost } from '../components/shared/url_helpers';
import { buildCheckoutPaths } from './checkoutPaths';

type Checkout = AffirmCheckoutBuilderFields.Fragment;

export function buildAffirmCheckout({ checkout, user }: {
  checkout: Checkout, user: Partial<IUser>,
}): Affirm.Checkout {
  const amounts = findAmounts(checkout);
  const guestEmail = checkout?.guest?.email;
  const buyerEmail = guestEmail ? guestEmail : user.email;

  return {
    merchant: buildMerchant(checkout),
    financing_program: financingProgram(checkout),
    items: checkout.orders.map(orderToItem),
    shipping: mapToAffirmContactAddress({
      address: shippingAddressOrDefault(checkout),
      buyerEmail,
    }),
    billing: mapToAffirmContactAddress({
      address: checkout.billingAddress,
      buyerEmail,
    }),
    currency: amounts.currency,
    shipping_amount: amounts.shippingAmount,
    tax_amount: amounts.taxAmount,
    total: amounts.total,
  };
}

function financingProgram(checkout: Checkout) {
  return checkout.paymentMethod.affirm?.financingProgramSlug;
}

function buildMerchant(checkout: Checkout): Affirm.Merchant {
  const checkoutPaths = buildCheckoutPaths(checkout.id);
  const displayedAmountOwed = findLineItemAmount(checkout, CheckoutLineItemType.AMOUNT_OWED);

  return {
    user_confirmation_url: withWebHost(checkoutPaths.redirectReturn({ displayedAmountOwed })),
    user_cancel_url: withWebHost(checkoutPaths.show()),
    user_confirmation_url_action: 'GET',
    public_api_key: AFFIRM_PUBLIC_API_KEY,
  };
}

function orderToItem(order: AffirmCheckoutBuilderFields.Orders): Affirm.Item {
  return {
    display_name: order.listing.title,
    sku: order.listing.id,
    qty: order.quantity,
    unit_price: order.subtotal.listingPrice.amountCents,
    item_image_url: order.listing.affirmImages[0]?.source,
    item_url: withWebHost(Paths.item.expand({ id: order.listing.id })),
  };
}

function mapToAffirmContactAddress({ address, buyerEmail }: {
  address: Address,
  buyerEmail: string,
}): Affirm.ContactAddress {
  return {
    name: {
      full: address.name,
    },
    address: {
      line1: address.streetAddress,
      line2: address.extendedAddress,
      city: address.locality,
      state: address.region,
      zipcode: address.postalCode,
      country: address.countryCode,
    },
    phone_number: address.phone,
    email: buyerEmail,
  };
}

/**
 * Shipping address will be blank for local pickup. This method defaults the
 * shipping address to the billing address in that case.
 */
function shippingAddressOrDefault(checkout: Checkout) {
  const isShippingAddressBlank = isEmpty(omitBy(checkout.shippingAddress, isEmpty));

  if (isShippingAddressBlank) {
    return checkout.billingAddress;
  }

  return checkout.shippingAddress;
}

function findAmounts(checkout: Checkout) {
  const shippingAmount = findLineItemAmount(checkout, CheckoutLineItemType.AMOUNT_SHIPPING);
  const taxAmount = findLineItemAmount(checkout, CheckoutLineItemType.AMOUNT_TAX);
  const totalAmount = findLineItemAmount(checkout, CheckoutLineItemType.AMOUNT_OWED);

  return {
    shippingAmount: shippingAmount.amountCents,
    taxAmount: taxAmount.amountCents,
    total: totalAmount.amountCents,
    currency: totalAmount.currency,
  };
}

function findLineItemAmount(checkout: Checkout, lineItemType: CheckoutLineItemType) {
  const lineItem = checkout.lineItems.find((item) => item.type === lineItemType);

  return lineItem?.amount || {};
}

export const AffirmCheckoutBuilderFragment = gql`
  fragment AffirmCheckoutBuilderFields on Checkout {
    _id
    id
    orders {
      quantity
      subtotal {
        listingPrice {
          amountCents
        }
      }
      listing {
        _id
        id
        title
        affirmImages: images(input: { transform: "card_square" }) {
          source
        }
      }
    }
    paymentMethod {
      affirm {
        financingProgramSlug
      }
    }
    shippingAddress {
      _id
      name
      streetAddress
      extendedAddress
      locality
      region
      postalCode
      countryCode
      phone
    }
    billingAddress {
      _id
      name
      streetAddress
      extendedAddress
      locality
      region
      postalCode
      countryCode
      phone
    }
    lineItems {
      type
      amount {
        amountCents
        currency
      }
    }
    guest {
      email
    }
  }
`;
